// src/context/AuthContext.js - Integrated with Your Backend
import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

// API URL from environment or default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setLoading(false);
        return;
      }

      // Verify token with backend
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          setUser(data.user);
        } else {
          await logout();
        }
      } else {
        await logout();
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      await logout();
    } finally {
      setLoading(false);
    }
  };

  // SIMPLE login: called from pages after successful /login API
  const login = (userData, token) => {
    try {
      setError(null);
      setUser(userData);

      if (userData) {
        localStorage.setItem('user', JSON.stringify(userData));
      }

      if (token) {
        localStorage.setItem('token', token);
        if (userData?.role === 'admin') {
          localStorage.setItem('adminToken', token);
        }
      }

      console.log(`✅ Logged in: ${userData?.email} (${userData?.role})`);
    } catch (err) {
      console.error('Login state update failed:', err);
      setError('Login state update failed');
    }
  };

  const signup = async (userData) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Signup failed');
      }

      const { user: newUser, token } = data;

      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('token', token);

      console.log(`✅ Signed up: ${newUser.email}`);

      return { success: true, user: newUser };
    } catch (err) {
      const errorMessage = err.message || 'Signup failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call backend logout
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
    } catch (err) {
      console.error('Logout API call failed:', err);
    } finally {
      // Clear state and storage regardless
      setUser(null);
      setError(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('adminToken');

      console.log('✅ Logged out');
    }
  };

  const updateUser = async (updatedData) => {
    try {
      setError(null);

      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(updatedData),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Update failed');
      }

      const updatedUser = data.profile;

      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));

      console.log(`✅ Profile updated: ${updatedUser.email}`);

      return { success: true, user: updatedUser };
    } catch (err) {
      const errorMessage = err.message || 'Update failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // In your auth context or middleware
const canAccessChat = (userRole) => {
  return ['admin', 'support_agent', 'user', 'guest'].includes(userRole);
};

const canResolveChat = (userRole) => {
  return ['admin', 'support_agent'].includes(userRole);
};

const canManageUsers = (userRole) => {
  return userRole === 'admin'; // Only admins
};

const canAssignChats = (userRole) => {
  return userRole === 'admin'; // Only admins can assign
};


  const isAdmin = user?.role === 'admin';
  const isClient = user?.role === 'client';
  const isAuthenticated = !!user;

  const value = {
    user,
    loading,
    error,
    login,       // simple setter (userData, token)
    logout,
    signup,
    updateUser,
    isAdmin,
    isClient,
    isAuthenticated,
    setError,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;